// Feasibility Data Adapters
// Maps existing government data sources to feasibility input format

import { YesNoUnknownValues } from '../types/feasibility.js';
import config from '../config/api.js';
import Tama38PolicyExtractor from './Tama38PolicyExtractor.js';
import BettermentLevyService from './BettermentLevyService.js';
// BuildingDataService is imported but used indirectly through the adapter function parameter
// eslint-disable-next-line no-unused-vars
import BuildingDataService from './BuildingDataService.js';

// Simple cache for market pricing data to prevent duplicate API calls
const marketDataCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
/** In-flight fetches keyed by address — dedupes prefetch + adaptToFeasibilityInput */
const inflightMarketFetches = new Map();

/**
 * Start market-data fetch in parallel with plans (fire-and-forget). Same request is deduped with getMarketPricing.
 * Skips גוש/חלקה-only strings; market API expects a street-style address.
 */
export function prefetchMarketPricing(address) {
  const key = typeof address === 'string' ? address.trim() : '';
  if (!key || key.startsWith('גוש ')) return;
  const cached = marketDataCache.get(key);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) return;
  if (inflightMarketFetches.has(key)) return;
  getMarketPricing(key).catch(() => {});
}

/**
 * Default cost constants based on Israeli construction market (2024)
 */
export const DEFAULT_COSTS = {
  // Construction costs per sqm (varies by quality and location)
  CONSTRUCTION_COST_PER_SQM: {
    basic: 7_000,      // Basic construction
    standard: 8_500,   // Standard construction  
    premium: 10_000    // Premium construction
  },
  
  // Parking costs per unit
  PARKING_COST_PER_UNIT: {
    surface: 30_000,     // Surface parking
    basement: 120_000,   // Underground parking (1 level)
    deepBasement: 180_000 // Deep underground (2+ levels)
  },
  
  // Relocation costs per existing unit
  RELOCATION_COST_PER_UNIT: 60_000,
  
  // Base fees and levies (varies significantly by municipality and project size)
  BASE_FEES_LEVIES: 2_000_000,
  
  // Owner bonus area (balcony, storage)
  OWNER_BONUS_AREA_PER_UNIT: 5,
  
  // Saleable ratio (typical for residential projects)
  SALEABLE_RATIO: 0.82,
  
  // Unit net GFA heuristic
  UNIT_NET_GFA: 95
};

/**
 * Municipality-specific TAMA 38 policies
 * TODO: This should be moved to a database and updated regularly
 */
export const MUNICIPALITY_POLICIES = {
  'תל אביב-יפו': {
    tama38ValidUntil: '2025-12-31',
    bettermentLevyRatePB: 0.25,
    preferredProgram: 'TAMA38_2'
  },
  'רמת גן': {
    tama38ValidUntil: '2025-12-31', 
    bettermentLevyRatePB: 0.25,
    preferredProgram: 'TAMA38_2'
  },
  'גבעתיים': {
    tama38ValidUntil: '2025-12-31',
    bettermentLevyRatePB: 0.5,
    preferredProgram: 'TAMA38_1'
  },
  'חולון': {
    tama38ValidUntil: '2025-12-31',
    bettermentLevyRatePB: 0.25,
    preferredProgram: 'TAMA38_2'
  },
  'בת ים': {
    tama38ValidUntil: '2025-12-31',
    bettermentLevyRatePB: 0.25,
    preferredProgram: 'TAMA38_2'
  },
  'ירושלים': {
    tama38ValidUntil: '2025-12-31',
    bettermentLevyRatePB: 0,
    preferredProgram: 'TAMA38_2'
  },
  'רחובות': {
    tama38ValidUntil: '2025-12-31',
    bettermentLevyRatePB: 0.25,
    preferredProgram: 'PINUY_BINUY'
  }
};

/**
 * Get real market pricing from market data API
 * @param {string} address - Full address string
 * @returns {Promise<Object>} Object with price and full data { price: number, data: Object }
 */
export async function getMarketPricing(address) {
  const cacheKey = typeof address === 'string' ? address.trim() : '';
  if (!cacheKey) return null;

  const cached = marketDataCache.get(cacheKey);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    console.log('💰 Using cached market pricing for:', cacheKey);
    return { price: cached.pricePerMeter, data: cached.data };
  }

  const existing = inflightMarketFetches.get(cacheKey);
  if (existing) {
    return existing;
  }

  const fetchPromise = (async () => {
    try {
      console.log('💰 Fetching real market data for:', cacheKey);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(`${config.apiBaseUrl}/api/market-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ address: cacheKey }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.log('⚠️ Market data API request failed:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('📊 Market data response:', data);

      if (data.success && data.pricingData && data.pricingData.pricePerMeter) {
        const pricePerMeter = data.pricingData.pricePerMeter;
        console.log(`💰 Real market pricing: ₪${pricePerMeter.toLocaleString()}/sqm`);
        marketDataCache.set(cacheKey, {
          pricePerMeter,
          data: data.pricingData,
          timestamp: Date.now(),
        });
        return { price: pricePerMeter, data: data.pricingData };
      }

      if (data.fallbackData && data.fallbackData.pricePerMeter) {
        console.log(
          `💰 Using market data fallback pricing: ₪${data.fallbackData.pricePerMeter.toLocaleString()}/sqm`
        );
        return { price: data.fallbackData.pricePerMeter, data: data.fallbackData };
      }

      return null;
    } catch (error) {
      console.error('❌ Market data API error:', error);
      return null;
    } finally {
      inflightMarketFetches.delete(cacheKey);
    }
  })();

  inflightMarketFetches.set(cacheKey, fetchPromise);
  return fetchPromise;
}

/**
 * Estimate market price per sqm based on municipality and area
 * Now integrates with market data for real data, falls back to estimates
 * @param {string} address - Full address string
 * @param {string} municipalityName - Municipality name
 * @param {Object[]} comps - Comparable sales data
 * @returns {Promise<Object>} Estimated price object { price: number, data: Object }
 */
export async function estimatePricePerSqm(address, municipalityName, comps = []) {
  // Try to get real data from market data API first
  const marketData = await getMarketPricing(address);
  if (marketData && marketData.price > 0) {
    return marketData;
  }
  
  // If we have comparable sales data, use median of recent sales
  if (comps && comps.length > 0) {
    const prices = comps
      .filter(comp => comp.newBuild !== false) // Prefer new construction comps
      .map(comp => comp.priceNIS / comp.areaSqm)
      .sort((a, b) => a - b);
    
    if (prices.length > 0) {
      const median = prices[Math.floor(prices.length / 2)];
      console.log(`💰 Using median comp price: ₪${median.toLocaleString()}/sqm from ${prices.length} comps`);
      return { 
        price: median, 
        data: { 
          dataSource: 'comps', 
          transactions: comps,
          note: 'Calculated from comparable sales'
        } 
      };
    }
  }
  
  // Fallback to municipality-based estimates (rough market data as of 2024)
  const marketPrices = {
    'תל אביב-יפו': 35_000,
    'רמת גן': 33_000,
    'גבעתיים': 32_000,
    'חולון': 28_000,
    'בת ים': 25_000,
    'ירושלים': 30_000,
    'רחובות': 22_000,
    'פתח תקווה': 29_000,
    'ראשון לציון': 26_000,
    'הרצליה': 40_000
  };
  
  const estimatedPrice = marketPrices[municipalityName] || 25_000; // Default fallback
  console.log(`💰 Using estimated market price for ${municipalityName}: ₪${estimatedPrice.toLocaleString()}/sqm`);
  return { 
    price: estimatedPrice, 
    data: { 
      dataSource: 'fallback_estimate', 
      pricePerMeter: estimatedPrice,
      note: 'Using city-wide average estimate' 
    } 
  };
}

/**
 * Check if TAMA 38 is valid in municipality
 * Enhanced to detect from actual planning data first, then fall back to policy database
 * @param {string} municipalityName - Municipality name
 * @param {Object[]} plans - Planning data from iPlan API
 * @returns {string} YesNoUnknown value
 */
export function isTamaValidInMunicipality(municipalityName, plans = []) {
  // FIRST: Check if we have actual TAMA 38 plans in the area (most accurate!)
  const tama38Detection = detectTama38FromPlans(plans, municipalityName);
  if (tama38Detection !== YesNoUnknownValues.UNKNOWN) {
    console.log(`🎯 TAMA 38 validity detected from actual plans: ${tama38Detection}`);
    return tama38Detection;
  }
  
  // FALLBACK: Use policy database (may be outdated)
  const policy = MUNICIPALITY_POLICIES[municipalityName];
  if (!policy || !policy.tama38ValidUntil) {
    return YesNoUnknownValues.UNKNOWN;
  }
  
  const validUntil = new Date(policy.tama38ValidUntil);
  const now = new Date();
  
  const isValid = now <= validUntil ? YesNoUnknownValues.YES : YesNoUnknownValues.NO;
  console.log(`📋 TAMA 38 validity from policy database for ${municipalityName}: ${isValid} (expires ${policy.tama38ValidUntil})`);
  return isValid;
}

/**
 * Detect TAMA 38 validity from actual planning data
 * @param {Object[]} plans - Planning data from iPlan API
 * @param {string} municipalityName - Municipality name
 * @returns {string} YesNoUnknown value
 */
export function detectTama38FromPlans(plans = [], municipalityName) {
  if (!plans || plans.length === 0) {
    return YesNoUnknownValues.UNKNOWN;
  }
  
  console.log(`🔍 Analyzing ${plans.length} plans for TAMA 38 validity in ${municipalityName}`);
  
  // Specific TAMA 38 keywords (more precise than general urban renewal)
  const tama38Keywords = [
    'תמ"א 38',
    'תמא 38', 
    'תמ״א 38',
    'תמא38',
    'tama 38',
    'תוכנית מתאר ארצית 38',
    'חיזוק מבנים',
    'חיזוק ועיבוי',
    'הוספת יחידות דיור'
  ];
  
  let activeTama38Plans = 0;
  let recentTama38Activity = false;
  
  for (const plan of plans) {
    const planName = plan.title || plan.pl_name || plan.name || plan.plan_name || '';
    const planObjectives = plan.objectives || plan.pl_objectives || plan.plan_objectives || '';
    const combinedText = `${planName} ${planObjectives}`.toLowerCase();
    
    // Check for TAMA 38 keywords
    const hasTama38Keyword = tama38Keywords.some(keyword => 
      combinedText.includes(keyword.toLowerCase())
    );
    
    if (hasTama38Keyword) {
      activeTama38Plans++;
      
      // Check if this is recent activity (approved/submitted in last 2 years)
      const approvalDate = plan.approvalDate || plan.pl_date_8;
      const submissionDate = plan.submissionDate || plan.pl_date_advertise;
      
      if (approvalDate || submissionDate) {
        const planDate = new Date(approvalDate || submissionDate);
        const twoYearsAgo = new Date();
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
        
        if (planDate > twoYearsAgo) {
          recentTama38Activity = true;
          console.log(`🏗️ Recent TAMA 38 activity detected: ${planName} (${approvalDate || submissionDate})`);
        }
      }
      
      console.log(`🎯 TAMA 38 plan found: "${planName}" - Status: ${plan.status || 'Unknown'}`);
    }
  }
  
  console.log(`📊 TAMA 38 Analysis for ${municipalityName}: ${activeTama38Plans} plans found, recent activity: ${recentTama38Activity}`);
  
  // Decision logic based on actual data
  if (activeTama38Plans > 0 && recentTama38Activity) {
    return YesNoUnknownValues.YES; // Active TAMA 38 with recent activity
  } else if (activeTama38Plans > 0) {
    return YesNoUnknownValues.UNKNOWN; // Has TAMA 38 plans but no recent activity
  } else {
    // No TAMA 38 plans found - check municipality patterns
    const knownActiveMunicipalities = [
      'תל אביב-יפו', 'גבעתיים', 'רמת גן', 'חולון', 'בת ים'
    ];
    
    if (knownActiveMunicipalities.includes(municipalityName)) {
      return YesNoUnknownValues.UNKNOWN; // Likely has TAMA 38 but no plans in this specific area
    } else {
      return YesNoUnknownValues.NO; // Unlikely to have TAMA 38
    }
  }
}

/**
 * Estimate building construction year based on architectural style and data
 * @param {Object} buildingData - Building data from municipal APIs
 * @returns {string} YesNoUnknown for pre-1980 eligibility
 */
export function estimatePre1980Eligibility(buildingData) {
  if (buildingData && buildingData.year) {
    const year = parseInt(buildingData.year);
    if (!isNaN(year)) {
      return year < 1980 ? YesNoUnknownValues.YES : YesNoUnknownValues.NO;
    }
  }
  
  // TODO: Add heuristics based on building characteristics
  // - Building height (low-rise more likely pre-1980)
  // - Construction type
  // - Neighborhood characteristics
  
  return YesNoUnknownValues.UNKNOWN;
}

/**
 * Derive effective floor count from building data.
 * Uses explicit floor count first, but cross-checks against height when both
 * are available — if the implied floor height exceeds 6 m the reported floor
 * count is treated as unreliable and re-derived from height / 3.6.
 * Falls back to height / 3.6 (truncated) when floors are absent.
 * Returns null if neither is available.
 */
function getEffectiveFloors(buildingData) {
  const floors = buildingData?.floors ? parseInt(buildingData.floors) : NaN;
  const height = buildingData?.height ? parseFloat(buildingData.height) : NaN;
  const hasFloors = !isNaN(floors) && floors > 0;
  const hasHeight = !isNaN(height) && height > 0;

  if (hasFloors && hasHeight) {
    const impliedFloorHeight = height / floors;
    if (impliedFloorHeight > 6) {
      const corrected = Math.floor(height / 3.6);
      if (corrected >= 1) {
        console.log(`⚠️ getEffectiveFloors — floor/height mismatch: ${floors} floors / ${height}m = ${impliedFloorHeight.toFixed(1)}m per floor. Using ${corrected} floors from height.`);
        return corrected;
      }
    }
    return floors;
  }

  if (hasFloors) return floors;

  if (hasHeight) {
    const derived = Math.floor(height / 3.6);
    return derived >= 1 ? derived : null;
  }

  return null;
}

/**
 * Estimate number of existing units based on building data.
 * Requires both footprint area AND floor count (or height) to produce an estimate.
 * Returns null when insufficient data — never guesses.
 * @param {Object} buildingData - Building data from municipal APIs
 * @returns {{ units: number, effectiveFloors: number, totalBuiltArea: number, unitsPerFloor: number, floorSource: string } | null}
 */
export function estimateExistingUnits(buildingData) {
  const footprint = buildingData?.area ? parseFloat(buildingData.area) : NaN;
  if (isNaN(footprint) || footprint <= 0) {
    console.log('🏠 Cannot estimate units — missing footprint area');
    return null;
  }

  const effectiveFloors = getEffectiveFloors(buildingData);
  if (effectiveFloors == null) {
    console.log('🏠 Cannot estimate units — missing floor count and building height');
    return null;
  }

  const unitsPerFloor = Math.round(footprint / 75);
  if (unitsPerFloor < 1) {
    console.log(`🏠 Cannot estimate units — footprint (${footprint} sqm) too small for even 1 unit per floor`);
    return null;
  }

  const units = unitsPerFloor * effectiveFloors;
  const totalBuiltArea = footprint * effectiveFloors;
  const rawFloors = buildingData?.floors ? parseInt(buildingData.floors) : NaN;
  const floorSource = (!rawFloors || isNaN(rawFloors) || rawFloors <= 0)
    ? 'height'
    : (rawFloors !== effectiveFloors ? 'corrected' : 'data');

  console.log(`🏠 Estimated ${units} units (${unitsPerFloor} units/floor × ${effectiveFloors} floors [${floorSource}${floorSource === 'corrected' ? `, reported: ${rawFloors}` : ''}], footprint: ${footprint} sqm, total built: ${Math.round(totalBuiltArea)} sqm)`);
  return { units, effectiveFloors, totalBuiltArea, unitsPerFloor, floorSource };
}

/**
 * Calculate average existing unit area from building data.
 * Uses the estimation result to avoid re-deriving floors/area.
 * Returns null when data is insufficient — never assumes 75 sqm.
 * @param {Object|null} estimation - Result from estimateExistingUnits()
 * @returns {number|null} Average unit area in sqm, or null
 */
export function estimateAvgUnitArea(estimation) {
  if (!estimation || !estimation.totalBuiltArea || !estimation.units || estimation.units <= 0) {
    return null;
  }
  const avgArea = estimation.totalBuiltArea / estimation.units;
  console.log(`📐 Calculated avg unit area: ${avgArea.toFixed(1)} sqm (${Math.round(estimation.totalBuiltArea)} sqm built / ${estimation.units} units)`);
  return avgArea;
}

/**
 * Estimate development rights GFA based on planning data
 * @param {Object} parcel - Parcel data with lot area
 * @param {Object} planningRights - Planning rights data
 * @returns {number} Estimated new GFA in sqm
 */
export function estimateNewGFA(parcel, planningRights) {
  // If we have explicit rights GFA from plans
  if (planningRights && planningRights.rightsGFAsqm) {
    console.log(`📋 Using explicit rights GFA: ${planningRights.rightsGFAsqm} sqm`);
    return planningRights.rightsGFAsqm;
  }
  
  // Calculate from FAR and lot area
  if (parcel && parcel.lotAreaSqm && planningRights && planningRights.far) {
    const estimatedGFA = parcel.lotAreaSqm * planningRights.far;
    console.log(`📋 Calculated GFA from FAR: ${estimatedGFA} sqm (${parcel.lotAreaSqm} × ${planningRights.far})`);
    return estimatedGFA;
  }
  
  // Fallback heuristic based on typical urban renewal projects
  if (parcel && parcel.lotAreaSqm) {
    // Assume FAR of 2.5-3.0 for urban renewal (conservative estimate)
    const conservativeFAR = 2.5;
    const estimatedGFA = parcel.lotAreaSqm * conservativeFAR;
    console.log(`📋 Using conservative FAR estimate: ${estimatedGFA} sqm (${parcel.lotAreaSqm} × ${conservativeFAR})`);
    return estimatedGFA;
  }
  
  // Last resort: typical project size
  return 3000;
}

/**
 * Main adapter function: Convert government data to FeasibilityInput
 * @param {string} address - Search address
 * @param {Object} parcel - GovMap parcel data
 * @param {Object} planningRights - Planning rights data
 * @param {Object} pbStatus - Pinuy-Binuy status
 * @param {Object} buildingData - Municipal building data
 * @param {Object[]} comps - Comparable sales data
 * @param {Object} overrides - Manual overrides for any field
 * @returns {Promise<Object>} FeasibilityInput object
 */
export async function adaptToFeasibilityInput(
  address,
  parcel,
  planningRights = {},
  pbStatus = {},
  buildingData = null,
  comps = [],
  overrides = {},
  plans = [], // Add plans parameter to use existing iPlan data!
  coordinates = null, // Add coordinates for building data lookup
  onStatusUpdate = null // Status update callback
) {
  console.log('🔄 Adapting government data to feasibility input...');
  
  // 🏗️ NEW: Get accurate building construction data from municipal sources!
  let enhancedBuildingData = buildingData;
  
  // First, check if we already have building data with construction year from existing sources
  if (buildingData && buildingData.year) {
    console.log('🎯 Using existing building data with construction year:', buildingData.year);
    enhancedBuildingData = {
      found: true,
      constructionYear: parseInt(buildingData.year),
      pre1980Eligible: parseInt(buildingData.year) < 1980,
      confidence: 'high',
      dataSource: buildingData.dataSource || 'tel_aviv_municipal',
      municipality: parcel?.municipalityName,
      buildingData: buildingData,
      buildingNumber: buildingData.buildingNumber,
      landUse: buildingData.landUse,
      buildingUseGroup: buildingData.buildingUseGroup,
      tama38Zone: buildingData.tama38Zone,
      conservationSite: buildingData.conservationSite,
      conservationArea: buildingData.conservationArea,
      floors: buildingData.floors,
      area: buildingData.area,
      height: buildingData.height
    };
  }
  // If no existing building data with year, try to fetch from municipal sources
  else if (coordinates && parcel?.municipalityName) {
    console.log('🏗️ No existing building data with year, attempting to fetch from municipal sources...');
    
    // For Tel Aviv, use parcel coordinates (Israeli Grid) instead of Web Mercator
    let municipalCoords = coordinates;
    if (parcel.municipalityName === 'תל אביב-יפו' && parcel.parcelX && parcel.parcelY) {
      municipalCoords = { x: parcel.parcelX, y: parcel.parcelY };
      console.log('🏛️ Using Israeli Grid coordinates for Tel Aviv:', municipalCoords);
    } else {
      console.log('🔍 Using original coordinates:', municipalCoords, 'for municipality:', parcel.municipalityName);
    }
    
    try {
      const municipalBuildingData = await BuildingDataService.getBuildingConstructionData(
        address,
        municipalCoords,
        parcel.municipalityName
      );
      
      if (municipalBuildingData && municipalBuildingData.found) {
        const conservationSite = enhancedBuildingData?.conservationSite || buildingData?.conservationSite;
        const conservationArea = enhancedBuildingData?.conservationArea || buildingData?.conservationArea;
        console.log('🎯 Using enhanced building data from municipal sources:', {
          constructionYear: municipalBuildingData.constructionYear,
          pre1980Eligible: municipalBuildingData.pre1980Eligible,
          confidence: municipalBuildingData.confidence,
          dataSource: municipalBuildingData.dataSource
        });
        enhancedBuildingData = {
          ...buildingData,
          ...municipalBuildingData,
          floors: buildingData?.floors || municipalBuildingData.floors,
          area: buildingData?.area || municipalBuildingData.area,
          height: buildingData?.height || municipalBuildingData.height,
          conservationSite,
          conservationArea
        };
      }
    } catch (error) {
      console.warn('⚠️ Failed to fetch enhanced building data, using fallback:', error);
    }
  } else {
    console.log('⚠️ Skipping building construction data fetch:', {
      hasCoordinates: !!coordinates,
      hasMunicipality: !!parcel?.municipalityName,
      hasExistingBuildingData: !!buildingData,
      buildingDataHasYear: !!(buildingData && buildingData.year),
      coordinates,
      municipality: parcel?.municipalityName
    });
  }
  
  // 🎯 NEW: Extract comprehensive TAMA 38 policy from existing iPLAN data!
  const municipalityPolicy = Tama38PolicyExtractor.getComprehensiveMunicipalityPolicy(plans, parcel?.municipalityName);
  console.log('🏛️ Extracted municipality policy from iPLAN:', {
    tama38Status: municipalityPolicy.tama38Status,
    activePlans: municipalityPolicy.activeTama38Plans,
    recentActivity: municipalityPolicy.recentTama38Activity,
    confidence: municipalityPolicy.policyConfidence
  });
  
  // 💰 NEW: Get real betterment levy rates!
  if (onStatusUpdate) onStatusUpdate('בודק אגרות השבחה...');
  const levyData = await BettermentLevyService.getBettermentLevyRates(parcel?.municipalityName);
  console.log('💰 Betterment levy rates retrieved:', {
    municipality: levyData.municipality,
    tamaRate: `${(levyData.tamaRate * 100).toFixed(0)}%`,
    source: levyData.dataSource,
    confidence: levyData.confidence
  });
  
  // Use existing planning data to detect urban renewal areas and conservation status!
  const detectedUrbanRenewal = detectUrbanRenewalFromPlans(plans, parcel?.municipalityName);
  const detectedConservation = !!enhancedBuildingData?.conservationSite?.isConservationSite;
  const section7778Plans = detectSection7778Plans(plans);
  const section23Plans = detectSection23Plans(plans);
  
  // Conservation status from Tel Aviv municipal GIS (layer 682)
  let buildingConservationStatus = null;
  if (enhancedBuildingData?.conservationSite?.isConservationSite) {
    buildingConservationStatus = {
      hasConservation: true,
      checked: true,
      source: 'tel_aviv_municipal',
      designation: enhancedBuildingData.conservationSite.designation,
      addresses: enhancedBuildingData.conservationSite.addresses,
      planNumber: enhancedBuildingData.conservationSite.planNumber,
      lastUpdated: enhancedBuildingData.conservationSite.lastUpdated,
      layerName: enhancedBuildingData.conservationSite.layerName
    };
  }
  
  // Ensure physical building properties survive all code paths
  if (enhancedBuildingData && buildingData) {
    if (!enhancedBuildingData.area && buildingData.area) enhancedBuildingData.area = buildingData.area;
    if (!enhancedBuildingData.height && buildingData.height) enhancedBuildingData.height = buildingData.height;
    if (!enhancedBuildingData.floors && buildingData.floors) enhancedBuildingData.floors = buildingData.floors;
  }

  // Estimate missing data points using enhanced building data
  const unitEstimation = overrides.existingUnits ? null : estimateExistingUnits(enhancedBuildingData);
  const estimatedUnits = overrides.existingUnits ?? (unitEstimation?.units ?? null);
  const avgUnitArea = overrides.avgExistingUnitAreaSqm ?? estimateAvgUnitArea(unitEstimation);

  console.log('━━━ UNIT ESTIMATION DEBUG ━━━');
  console.log('  Input data:', {
    floors: enhancedBuildingData?.floors ?? null,
    area: enhancedBuildingData?.area ?? null,
    height: enhancedBuildingData?.height ?? null,
    overrideUnits: overrides.existingUnits ?? null,
    overrideAvgArea: overrides.avgExistingUnitAreaSqm ?? null
  });
  if (unitEstimation) {
    console.log('  Estimation result:', {
      units: unitEstimation.units,
      unitsPerFloor: unitEstimation.unitsPerFloor,
      effectiveFloors: unitEstimation.effectiveFloors,
      floorSource: unitEstimation.floorSource,
      totalBuiltArea: Math.round(unitEstimation.totalBuiltArea),
      avgUnitArea: avgUnitArea ? Math.round(avgUnitArea * 10) / 10 : null
    });
  } else if (overrides.existingUnits) {
    console.log('  Using user override:', overrides.existingUnits, 'units');
  } else {
    console.log('  Estimation result: null (insufficient data)');
  }
  console.log('  Final → existingUnits:', estimatedUnits, '| avgUnitArea:', avgUnitArea);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  // Convert boolean to string for Zod validation
  let pre1980Eligibility = overrides.pre1980Eligibility;
  if (pre1980Eligibility === undefined) {
    if (enhancedBuildingData?.pre1980Eligible === true) {
      pre1980Eligibility = 'Yes';
    } else if (enhancedBuildingData?.pre1980Eligible === false) {
      pre1980Eligibility = 'No';
    } else {
      pre1980Eligibility = estimatePre1980Eligibility(enhancedBuildingData);
    }
  }
  
  // 🎯 Use extracted TAMA 38 policy instead of static database!
  const tamaValid = overrides.tama38ValidInMunicipality ?? municipalityPolicy.tama38Status;
  if (onStatusUpdate) onStatusUpdate('מביא נתוני מחירי שוק...');
  
  let pricePerSqm = overrides.expectedSalePricePerSqmNIS;
  let marketData = null;
  
  if (!pricePerSqm) {
    const estimation = await estimatePricePerSqm(address, parcel?.municipalityName, comps);
    pricePerSqm = estimation.price;
    marketData = estimation.data;
  }
  
  const estimatedGFA = overrides.estimatedNewGFAsqm ?? estimateNewGFA(parcel, planningRights);
  
  const feasibilityInput = {
    // Identity
    address: address,
    municipalityName: parcel?.municipalityName || 'לא ידוע',
    municipalityCode: parcel?.municipalityCode,
    
    // Parcel
    gush: parcel?.gush,
    helka: parcel?.helka,
    lotAreaSqm: parcel?.lotAreaSqm,
    
    // Existing building
    existingUnits: estimatedUnits,
    avgExistingUnitAreaSqm: avgUnitArea,
    pre1980Eligibility: pre1980Eligibility,
    constructionYear: enhancedBuildingData?.constructionYear || null,
    constructionYearConfidence: enhancedBuildingData?.confidence || 'unknown',
    yearEstimated: false,
    landUse: enhancedBuildingData?.landUse || buildingData?.landUse || null,
    buildingUseGroup: enhancedBuildingData?.buildingUseGroup || buildingData?.buildingUseGroup || null,
    tama38Zone: enhancedBuildingData?.tama38Zone || buildingData?.tama38Zone || null,
    
    // Planning/policy - NOW USING REAL DATA!
    tama38ValidInMunicipality: tamaValid,
    declaredPinuyBinuyArea: overrides.declaredPinuyBinuyArea ?? detectedUrbanRenewal, // Use detected data!
    conservationListed: overrides.conservationListed ?? detectedConservation, // Use detected conservation status!
    section7778Plans: section7778Plans, // Section 77/78 building permit freeze plans
    section23Plans: section23Plans, // Section 23 TAMA 38 continued urban renewal plans
    buildingConservationStatus: buildingConservationStatus, // Building-specific conservation from municipal GIS (Tel Aviv)
    
    // Development rights
    estimatedNewGFAsqm: estimatedGFA,
    saleableRatio: DEFAULT_COSTS.SALEABLE_RATIO,
    
    // Market (now with real market data!)
    expectedSalePricePerSqmNIS: pricePerSqm,
    marketData: marketData, // 💰 Full market data object for UI
    
    // Costs (now with REAL betterment levy rates!)
    constructionCostPerSqmNIS: DEFAULT_COSTS.CONSTRUCTION_COST_PER_SQM.standard,
    parkingCostPerNewUnitNIS: DEFAULT_COSTS.PARKING_COST_PER_UNIT.basement,
    relocationCostPerExistingUnitNIS: DEFAULT_COSTS.RELOCATION_COST_PER_UNIT,
    feesLeviesPermitsTotalNIS: DEFAULT_COSTS.BASE_FEES_LEVIES,
    
    // 💰 NEW: Real betterment levy data
    bettermentLevyRate: levyData.tamaRate,
    bettermentLevySource: levyData.dataSource,
    bettermentLevyConfidence: levyData.confidence,
    
    // Owner package
    ownerBonusAreaPerUnitSqm: DEFAULT_COSTS.OWNER_BONUS_AREA_PER_UNIT,
    
    // Modeling params
    targetDeveloperProfitPct: 0.18,
    unitNetGfaHeuristicSqm: DEFAULT_COSTS.UNIT_NET_GFA,
    
    // Apply any manual overrides
    ...overrides
  };
  
  console.log('✅ Feasibility input adapted with REAL data sources:', {
    address: feasibilityInput.address,
    municipality: feasibilityInput.municipalityName,
    units: feasibilityInput.existingUnits,
    avgArea: feasibilityInput.avgExistingUnitAreaSqm,
    pre1980: feasibilityInput.pre1980Eligibility,
    constructionYear: feasibilityInput.constructionYear,
    landUse: feasibilityInput.landUse,
    tama38Zone: feasibilityInput.tama38Zone,
    tamaValid: feasibilityInput.tama38ValidInMunicipality,
    declaredPB: feasibilityInput.declaredPinuyBinuyArea,
    conservationListed: feasibilityInput.conservationListed,
    estimatedGFA: feasibilityInput.estimatedNewGFAsqm,
    pricePerSqm: feasibilityInput.expectedSalePricePerSqmNIS,
    plansAnalyzed: plans?.length || 0,
    section7778PlansCount: feasibilityInput.section7778Plans?.length || 0,
    section23PlansCount: feasibilityInput.section23Plans?.length || 0,
    // 🎯 Policy data from iPLAN analysis
    policyData: {
      tama38Plans: municipalityPolicy.activeTama38Plans,
      recentActivity: municipalityPolicy.recentTama38Activity,
      confidence: municipalityPolicy.policyConfidence,
      lastApproval: municipalityPolicy.lastTama38Approval
    },
    // 💰 NEW: Real betterment levy data
    bettermentLevy: {
      rate: `${(levyData.tamaRate * 100).toFixed(0)}%`,
      source: levyData.dataSource,
      confidence: levyData.confidence,
      notes: levyData.notes
    },
    detectedUrbanRenewal: detectedUrbanRenewal,
    detectedConservation: detectedConservation,
    section7778Plans: section7778Plans,
    section23Plans: section23Plans,
    buildingConservationStatus: buildingConservationStatus
  });
  
  return feasibilityInput;
}

/**
 * Check if address is in declared Pinuy-Binuy area based on existing planning data
 * @param {Object[]} plans - Planning data from iPlan API (from your existing calls!)
 * @param {string} municipalityName - Municipality name
 * @returns {string} YesNoUnknown value
 */
export function detectUrbanRenewalFromPlans(plans = [], municipalityName) {
  if (!plans || plans.length === 0) {
    return YesNoUnknownValues.UNKNOWN;
  }
  
  console.log(`🏗️ Analyzing ${plans.length} plans for urban renewal detection`);
  console.log(`🏗️ First plan structure:`, Object.keys(plans[0] || {}));
  
  // Keywords that indicate urban renewal/Pinuy-Binuy areas in Israeli planning documents
  const urbanRenewalKeywords = [
    'התחדשות עירונית',
    'פינוי בינוי', 
    'פינוי-בינוי',
    'תמ"א 38',
    'תמא 38',
    'חיזוק מבנים',
    'התחדשות שכונתית',
    'הגדלת זכויות בניה',
    'צפיפות מגורים'
  ];
  
  // Note: Conservation detection is now handled by detectConservationFromPlans() function
  // which provides more sophisticated analysis of planning data
  
  let hasConservationStatus = false;
  let hasUrbanRenewalPlan = false;
  
  // Check plan names and objectives for both conservation and urban renewal keywords
  for (const plan of plans) {
    const planName = plan.title || plan.pl_name || plan.name || plan.plan_name || '';
    const planObjectives = plan.objectives || plan.pl_objectives || plan.plan_objectives || '';
    const combinedText = `${planName} ${planObjectives}`.toLowerCase();
    
    console.log(`🔍 Analyzing plan: "${planName}" with objectives: "${planObjectives.substring(0, 100)}..."`);
    
    // Use the improved conservation detection logic
    const isConservationPlan = detectConservationFromPlans([plan]);
    if (isConservationPlan) {
      console.log(`🏛️ CONSERVATION DETECTED: ${planName || 'Unnamed plan'} - This may block urban renewal!`);
      hasConservationStatus = true;
    }
    
    // Check for urban renewal keywords
    const hasUrbanRenewalKeyword = urbanRenewalKeywords.some(keyword => 
      combinedText.includes(keyword.toLowerCase())
    );
    
    if (hasUrbanRenewalKeyword) {
      console.log(`🏗️ Found urban renewal plan: ${planName || 'Unnamed plan'} (keyword detected in ${planName ? 'title' : 'objectives'})`);
      hasUrbanRenewalPlan = true;
    }
  }
  
  // Return results based on what we found
  if (hasConservationStatus) {
    console.log(`🚫 CONSERVATION STATUS DETECTED - Urban renewal may be blocked!`);
    return YesNoUnknownValues.NO; // Conservation typically blocks urban renewal
  }
  
  if (hasUrbanRenewalPlan) {
    return YesNoUnknownValues.YES;
  }
  
  // Check municipality-specific policies for areas with active programs
  const municipalitiesWithActiveUR = [
    'תל אביב-יפו',
    'גבעתיים', 
    'רמת גן',
    'בת ים',
    'חולון',
    'ירושלים'
  ];
  
  if (municipalitiesWithActiveUR.includes(municipalityName)) {
    // These municipalities have active urban renewal programs
    // but we didn't find specific plans, so it's possible but unknown
    return YesNoUnknownValues.UNKNOWN;
  }
  
  return YesNoUnknownValues.NO;
}

/**
 * Check if building has conservation/heritage protection status from planning data
 * @param {Object[]} plans - Planning data from iPlan API
 * @returns {boolean} True if conservation status detected
 */
export function detectConservationFromPlans(plans = []) {
  if (!plans || plans.length === 0) {
    return false;
  }
  
  console.log(`🏛️ Analyzing ${plans.length} plans for conservation status`);
  
  // SPECIFIC conservation keywords that indicate BUILDING-LEVEL protection
  const specificConservationKeywords = [
    'בניין מוגן',           // Protected building (most specific)
    'אתר מוגן',             // Protected site
    'הכרזה על בניין מוגן',  // Declaration of protected building
    'מבנה לשימור',          // Building for conservation
    'מבנה היסטורי',         // Historical building
    'בניין היסטורי',        // Historical building
    'מבנה מוגן',            // Protected structure
    'שימור מבנה ספציפי',     // Specific building conservation
    'הגנה על מבנה',         // Building protection
    'בניין בעל ערך',        // Building of value
    'מבנה בעל ערך'          // Structure of value
  ];
  
  // GENERAL area conservation keywords that DON'T block urban renewal
  const generalConservationKeywords = [
    'תוכנית שימור כללית',    // General conservation plan
    'שימור אזור',            // Area conservation (too broad)
    'שימור נוף',             // Landscape preservation
    'שימור סביבה',           // Environmental preservation
    'שימור אופי',            // Character preservation (general)
    'שימור מתחם',            // Complex preservation (could be general)
    'שימור עירונית',         // Urban preservation (too broad)
    'תוכנית מתאר',           // Master plan (general planning)
    'הוראות כלליות',         // General guidelines
    'עקרונות שימור'          // Conservation principles (general)
  ];
  
  // Context exclusions - these indicate the word "שימור" is NOT about heritage
  const exclusionContexts = [
    'שימור וחיזוק מעמד',      // Preserving and strengthening status
    'שימור מעמד',            // Preserving status
    'שימור זכויות',          // Preserving rights
    'שימור איכות',           // Preserving quality
    'שימור סביבה',           // Environmental preservation
    'שימור נוף',             // Landscape preservation
    'שימור קרקע',            // Soil preservation
    'שימור משאבים',          // Resource preservation
    'שימור יעוד',            // Preserving designation
    'שימור תכנון',           // Planning preservation
    'שימור פתוח',            // Open space preservation
    'שימור ירוק',            // Green preservation
    'שימור כללי',            // General preservation
    'הוראות כלליות לשימור'   // General conservation guidelines
  ];
  
  for (const plan of plans) {
    const planName = plan.title || plan.pl_name || plan.name || plan.plan_name || '';
    const planObjectives = plan.objectives || plan.pl_objectives || plan.plan_objectives || '';
    const planType = plan.planSubtype || plan.entity_subtype_desc || '';
    const combinedText = `${planName} ${planObjectives} ${planType}`.toLowerCase();
    
    console.log(`🔍 Checking plan: "${planName}" for conservation keywords`);
    
    // First check for exclusion contexts (non-heritage conservation)
    const hasExclusionContext = exclusionContexts.some(exclusion => 
      combinedText.includes(exclusion.toLowerCase())
    );
    
    if (hasExclusionContext) {
      console.log(`⚪ Plan contains "שימור" but in non-heritage context: ${planName}`);
      continue; // Skip this plan - it's not heritage conservation
    }
    
    // Check for general area conservation (doesn't block urban renewal)
    const hasGeneralConservation = generalConservationKeywords.some(keyword => 
      combinedText.includes(keyword.toLowerCase())
    );
    
    if (hasGeneralConservation) {
      console.log(`🌍 Plan is general area conservation (doesn't block urban renewal): ${planName}`);
      continue; // Skip - general conservation doesn't block urban renewal
    }
    
    // Check for POLICY plans ABOUT conservation buildings (not conservation designations)
    // These are plans about exemptions, benefits, or regulations for already-conserved buildings
    const policyPlanKeywords = [
      'פטור מתקן חניה למבנה',  // Parking exemption for building
      'מתן פטור למבנה',         // Granting exemption to building
      'הקלה למבנה לשימור',      // Relief for conservation building
      'תקנון חניה',             // Parking regulations (when with conservation)
      'מדיניות',                // Policy (when with conservation)
      'הוראות למבנה לשימור'     // Instructions for conservation building
    ];
    
    const isPolicyPlan = policyPlanKeywords.some(keyword => 
      combinedText.includes(keyword.toLowerCase())
    );
    
    if (isPolicyPlan) {
      console.log(`📋 Plan is a policy ABOUT conservation buildings (not a designation): ${planName}`);
      continue; // Skip - this is a policy plan, not a conservation designation
    }
    
    // Check for SPECIFIC building conservation keywords
    const hasSpecificConservation = specificConservationKeywords.some(keyword => 
      combinedText.includes(keyword.toLowerCase())
    );
    
    if (hasSpecificConservation) {
      console.log(`🏛️ SPECIFIC building conservation detected: ${planName || 'Unnamed plan'}`);
      return true;
    }
    
    // Special case: Check if "שימור" appears with building-specific context
    if (combinedText.includes('שימור')) {
      const buildingSpecificContexts = [
        'שימור מבנה ספציפי', 'שימור בניין ספציפי', 'שימור מבנה קיים',
        'שימור חזית מבנה', 'שימור קומת קרקע', 'שימור מרפסת',
        'הגנה על מבנה קיים', 'שימור אדריכלות המבנה'
      ];
      
      const hasBuildingSpecificContext = buildingSpecificContexts.some(context => 
        combinedText.includes(context.toLowerCase())
      );
      
      if (hasBuildingSpecificContext) {
        console.log(`🏛️ Building-specific conservation detected: ${planName}`);
        return true;
      }
      
      // If it's just "תוכנית שימור" without building-specific context, it's likely general
      if (combinedText.includes('תוכנית שימור') && !hasBuildingSpecificContext) {
        console.log(`🌍 General conservation plan (not building-specific): ${planName}`);
        continue;
      }
    }
  }
  
  console.log(`✅ No SPECIFIC building conservation detected in ${plans.length} plans`);
  return false;
}

/**
 * Detect Section 77/78 building permit freeze plans
 * @param {Array} plans - Array of architectural plans
 * @returns {Array} Array of plans that mention Section 77/78
 */
function detectSection7778Plans(plans = []) {
  if (!plans || plans.length === 0) {
    return [];
  }
  
  console.log(`🚫 Analyzing ${plans.length} plans for Section 77/78 (building permit freeze)`);
  
  // Keywords that indicate Section 77/78
  const section7778Keywords = [
    'סעיף 77',
    'סעיף 78',
    'סעיף 77-78',
    'עוצר היתרים',
    'עיצור היתרים',
    'תנאים מגבילים 77',
    'תנאים מגבילים 78',
    'פרסום תנאים מגבילים 77',
    'פרסום תנאים מגבילים 78'
  ];
  
  const matchingPlans = [];
  
  for (const plan of plans) {
    const fieldsToCheck = [
      plan.title,
      plan.objectives,
      plan.status,
      plan.planType,
      plan.notes
    ].filter(field => field).join(' ').toLowerCase();
    
    // Check if any keyword matches
    const hasSection7778 = section7778Keywords.some(keyword => 
      fieldsToCheck.includes(keyword.toLowerCase())
    );
    
    if (hasSection7778) {
      console.log(`🚫 Section 77/78 detected in plan: ${plan.title || plan.planNumber}`);
      matchingPlans.push({
        planNumber: plan.planNumber || plan.title || '',
        title: plan.title || '',
        status: plan.status || '',
        approvalDate: plan.approvalDate || ''
      });
    }
  }
  
  if (matchingPlans.length > 0) {
    console.log(`🚫 Found ${matchingPlans.length} plans with Section 77/78 restrictions`);
  } else {
    console.log(`✅ No Section 77/78 restrictions found`);
  }
  
  return matchingPlans;
}

/**
 * NOTE: detectConservationPlans function removed - we now use Puppeteer-based
 * building archive checking instead of plans-based detection for more accuracy
 */

/**
 * Detect Section 23 TAMA 38 plans (allows municipalities to continue urban renewal)
 * @param {Array} plans - Array of architectural plans
 * @returns {Array} Array of Section 23 plans with details
 */
function detectSection23Plans(plans = []) {
  if (!plans || plans.length === 0) {
    return [];
  }

  console.log(`✨ Analyzing ${plans.length} plans for Section 23 TAMA 38 (continued urban renewal)`);
  
  // Debug: Show all plan numbers to help identify Section 23 plans
  const allPlanNumbers = plans
    .filter(p => p.planNumber || p.id)
    .slice(0, 10)
    .map(p => `${p.title} → ${p.planNumber || p.id}`)
    .join(', ');
  console.log(`📋 Sample plan numbers: ${allPlanNumbers}`);

  // Keywords that indicate Section 23 TAMA 38 plans
  const section23Keywords = [
    'סעיף 23',
    'סעיף 23 לתמ"א 38',
    'סעיף 23 לתמ"א38',
    'תמ"א 38 סעיף 23',
    'תמ"א38 סעיף 23'
  ];

  // Known Section 23 TAMA 38 plan numbers from government list
  // Source: https://www.gov.il/he/pages/tama38-changes-steps?chapterIndex=4
  const knownSection23Plans = [
    'תא/3616/א',
    'תא/3616א',
    '3616/א',
    '3616א'
    // Add more as discovered
  ];

  const matchingPlans = [];

  for (const plan of plans) {
    const fieldsToCheck = [
      plan.title,
      plan.objectives,
      plan.status,
      plan.planType,
      plan.notes,
      plan.planNumber
    ].filter(field => field).join(' ').toLowerCase();

    // Check if any keyword matches
    const hasSection23Keyword = section23Keywords.some(keyword => 
      fieldsToCheck.includes(keyword.toLowerCase())
    );
    
    // Check if plan number matches known Section 23 plans
    const planNumber = (plan.planNumber || plan.title || '').trim();
    const hasSection23PlanNumber = knownSection23Plans.some(knownPlan => {
      const normalizedKnown = knownPlan.replace(/\s+/g, '').toLowerCase();
      const normalizedPlan = planNumber.replace(/\s+/g, '').toLowerCase();
      return normalizedPlan.includes(normalizedKnown);
    });

    if (hasSection23Keyword || hasSection23PlanNumber) {
      console.log(`✨ Section 23 TAMA 38 detected in plan: ${plan.title || plan.planNumber} (planNumber: ${plan.planNumber})`);
      matchingPlans.push({
        planNumber: plan.planNumber || plan.title || '',
        title: plan.title || '',
        status: plan.status || '',
        approvalDate: plan.approvalDate || ''
      });
    }
  }

  if (matchingPlans.length > 0) {
    console.log(`✨ Found ${matchingPlans.length} plans with Section 23 TAMA 38 (continued urban renewal)`);
  } else {
    console.log(`✅ No Section 23 TAMA 38 plans found`);
  }

  return matchingPlans;
}
